import { supabase } from "@/integrations/supabase/client";
import { checkPlanLimits } from "@/lib/planLimits";
import { applyAgentDefaults, getDefaultFormFields } from "@/services/agentDefaults";

// Define TemplateAgent interface for agents table
interface TemplateAgent {
  id: string;
  title: string;
  description?: string | null;
  agent_name?: string | null;
  organization_id: string;
  created_by: string;
  instructions?: string | null;
  welcome_message?: string | null;
  conversation_starters?: string[] | null;
  thank_you_message?: string | null;
  restrictions?: string | null;
  hidden_template_instructions?: string | null;
  avatar_url?: string | null;
  banner?: string | null;
  splash_page_title?: string | null;
  splash_page_subtitle?: string | null;
  start_button_text?: string | null;
  ai_temperature?: number | null;
  notification_email?: string | null;
  notification_webhook_url?: string | null;
  is_template?: boolean | null;
  is_active?: boolean | null;
  is_public?: boolean | null;
  persona_id?: string | null;
  industry_id?: string | null;
  template_source_id?: string | null;
  category?: string | null;
  public_id?: string | null;
  created_at: string;
  updated_at: string;
  fts?: unknown;
  [key: string]: any;
}

// Memoize the Supabase URLs and keys
const getSupabaseUrls = (() => {
  // Cache the computed values
  let supabaseUrl: string | undefined;
  let functionsUrl: string | undefined;
  let anonKey: string | undefined;

  return () => {
    // Return cached values if already computed
    if (supabaseUrl && functionsUrl && anonKey) {
      return { supabaseUrl, functionsUrl, anonKey };
    }

    // Compute values once
    supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    functionsUrl = supabaseUrl?.replace("supabase.co", "functions.supabase.co");
    anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    return { supabaseUrl, functionsUrl, anonKey };
  };
})();

// Cache for template agent data
interface TemplateCache {
  timestamp: number;
  data: TemplateAgent[];
}

// Cache storage with 5-minute expiration
let templateAgentCache: TemplateCache | null = null;
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Cache for API calls with specific search parameters
const recentSearchCache: Map<
  string,
  {
    timestamp: number;
    result: {
      data?: unknown[];
      conversationCounts?: Record<string, number>;
      error?: unknown;
    };
  }
> = new Map();

// Cache for regular agent data
interface AgentCache {
  timestamp: number;
  data: TemplateAgent[];
}

// Cache storage for regular agents with 5-minute expiration
let agentCache: AgentCache | null = null;

// Type guard for TemplateAgent
function isTemplateAgent(obj: any): obj is TemplateAgent {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.id === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.organization_id === 'string' &&
    typeof obj.created_by === 'string'
  );
}

export const fetchTemplateAgents = async (
  params: { is_template?: boolean; search?: string; category?: string } = {
    is_template: true,
  }
) => {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    // If we're fetching all templates (no search/category filter) and we have a valid cache, use it
    const isBasicFetch =
      params.is_template === true && !params.search && !params.category;
    if (
      isBasicFetch &&
      templateAgentCache &&
      Date.now() - templateAgentCache.timestamp < CACHE_EXPIRY_MS
    ) {
      console.log("[templateService] Using cached template agents");
      return { data: templateAgentCache.data };
    }

    // Check for recent identical search in cache
    const cacheKey = JSON.stringify(params);
    const cachedSearch = recentSearchCache.get(cacheKey);
    if (cachedSearch && Date.now() - cachedSearch.timestamp < CACHE_EXPIRY_MS) {
      console.log("[templateService] Using cached search result");
      return cachedSearch.result;
    }

    const response = await fetch(`${functionsUrl}/agent-service`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    // Cache the result
    if (!result.error) {
      // Store in recent search cache
      recentSearchCache.set(cacheKey, {
        timestamp: Date.now(),
        result,
      });

      // If this is a basic fetch, update the template cache
      if (isBasicFetch) {
        templateAgentCache = {
          timestamp: Date.now(),
          data: result.data || [],
        };
      }

      // Clean up old cache entries (keep the cache size reasonable)
      if (recentSearchCache.size > 20) {
        const now = Date.now();
        for (const [key, value] of recentSearchCache.entries()) {
          if (now - value.timestamp > CACHE_EXPIRY_MS) {
            recentSearchCache.delete(key);
          }
        }
      }
    }

    if (result.error) {
      console.error(
        "Error fetching template agents (edge function):",
        result.error
      );
    }

    return result;
  } catch (error: unknown) {
    console.error("Error fetching template agents (edge function):", error);
    return { error };
  }
};

// Clear the cache - useful when templates might have been updated
export const clearTemplateCache = () => {
  templateAgentCache = null;
  recentSearchCache.clear();
  console.log("[templateService] Template cache cleared");
};

/**
 * @deprecated Use duplicateTemplateToAgent instead. This function only copies form fields but not behaviors.
 * Will be removed in a future version once all usage is migrated to duplicateTemplateToAgent.
 */
export const createAgentFromTemplate = async (
  templateData: TemplateAgent,
  organizationId: string,
  userId: string
) => {
  try {
    console.log("Creating agent from template:", templateData.title);

    // Extract fields that should be kept from the original template
    const {
      id: templateId,
      is_template,
      public_id,
      fts, // Also exclude the fts field which is generated by the database
      ...templateFields
    } = templateData;

    // Create a new agent based on the template
    const { data: newAgent, error } = await supabase
      .from("agents")
      .insert({
        ...templateFields,
        organization_id: organizationId,
        created_by: userId,
        is_template: false,
        category: templateFields.category || "unknown", // Default to 'unknown' if no category
        banner: templateFields.banner || null, // Make sure banner is copied from the template
        // Generate a new public_id instead of copying from the template
        public_id: crypto.randomUUID(),
      })
      .select("*")
      .single();

    if (error) {
      console.error("Error creating agent from template:", error);
      return { error };
    }

    // Copy form fields if any exist
    try {
      const { data: formFields } = await supabase
        .from("form_fields")
        .select("*")
        .eq("agent_id", templateId);

      if (formFields && formFields.length > 0) {
        console.log(`Copying ${formFields.length} form fields from template`);

        const formFieldsToInsert = formFields.map((field) => {
          // Exclude the id from the original field
          const { id, agent_id, ...fieldData } = field;

          // Return a new field object with the new agent_id
          return {
            ...fieldData,
            agent_id: newAgent.id,
          };
        });

        const { error: formFieldsError } = await supabase
          .from("form_fields")
          .insert(formFieldsToInsert);

        if (formFieldsError) {
          console.error("Error copying form fields:", formFieldsError);
          // Continue anyway, as the agent was created successfully
        }
      }
    } catch (formFieldsErr) {
      console.error("Error handling form fields:", formFieldsErr);
      // Continue anyway, as the agent was created successfully
    }

    // Clear the cache since templates may have changed
    clearTemplateCache();

    return { data: newAgent };
  } catch (error) {
    console.error("Exception in createAgentFromTemplate:", error);
    return { error };
  }
};

/**
 * Creates a new template agent
 */
export const createTemplateAgent = async (
  templateData: Partial<TemplateAgent> & {
    title: string;
    created_by: string;
    organization_id: string;
  }
) => {
  try {
    // Make sure we're creating a template
    const agentData = {
      ...templateData,
      is_template: true,
      public_id: crypto.randomUUID(), // Generate UUID for public access
      is_active: true,
      category: templateData.category || "unknown", // Default to 'unknown' if no category
      banner: templateData.banner || null, // Make sure banner is null if not provided
    };

    const { data, error } = await supabase
      .from("agents")
      .insert(agentData)
      .select("*")
      .single();

    if (error) {
      console.error("Error creating template agent:", error);
      return { error };
    }

    // Clear the cache since templates have changed
    clearTemplateCache();

    return { data };
  } catch (error: unknown) {
    console.error("Error creating template agent:", error);
    return { error };
  }
};

// Add the banners to the template agents
export const updateTemplateAgentBanners = async () => {
  try {
    // Update CPA Firms template with the tax form image
    await supabase
      .from("agents")
      .update({
        banner: "/lovable-uploads/f01a31b6-aaa4-4eb7-8831-868203cc4044.png",
      })
      .eq("title", "Lead Generation – CPA Firms")
      .eq("is_template", true);

    // Update General Law Firm template with the scales of justice image
    await supabase
      .from("agents")
      .update({
        banner: "/lovable-uploads/3a2f3bd0-41de-48a9-a33d-683d10bec9d8.png",
      })
      .eq("title", "Lead Generation – General Law Firm")
      .eq("is_template", true);

    // Update Immigration Law template with the passport image
    await supabase
      .from("agents")
      .update({
        banner: "/lovable-uploads/2fdff863-ca1c-46ff-b1c7-b570fd70f17c.png",
      })
      .eq("title", "Lead Generation – Immigration Law")
      .eq("is_template", true);

    // Clear the cache since templates have changed
    clearTemplateCache();

    return { success: true };
  } catch (error) {
    console.error("Error updating template banners:", error);
    return { error };
  }
};

export const fetchAgents = async (
  organizationId: string,
  params: { search?: string; includeConversationCounts?: boolean; includeArchived?: boolean } = {}
) => {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    const isBasicFetch = !params.search && !params.includeArchived;

    // If we're fetching all agents (no search filter and not including archived) and we have a valid cache, use it
    if (
      isBasicFetch &&
      agentCache &&
      Date.now() - agentCache.timestamp < CACHE_EXPIRY_MS
    ) {
      console.log("[templateService] Using cached agents");
      // Return the cached data with empty conversation counts if not in cache
      const cachedResult = recentSearchCache.get(
        `agents_${organizationId}_${JSON.stringify(params)}`
      )?.result;
      const filteredAgents = Array.isArray(agentCache.data)
        ? agentCache.data.filter(isTemplateAgent)
        : [];
      return {
        data: filteredAgents,
        conversationCounts: cachedResult?.conversationCounts || {},
      };
    }

    // Check for recent identical search in cache
    const cacheKey = `agents_${organizationId}_${JSON.stringify(params)}`;
    const cachedSearch = recentSearchCache.get(cacheKey);
    if (cachedSearch && Date.now() - cachedSearch.timestamp < CACHE_EXPIRY_MS) {
      console.log("[templateService] Using cached agent search result");
      // Ensure conversationCounts is always present in the result
      return {
        ...cachedSearch.result,
        conversationCounts: cachedSearch.result.conversationCounts || {},
      };
    }

    // If no cache hit, fetch from database
    let query = supabase
      .from("agents")
      .select("*" as any)
      .eq("organization_id", organizationId)
      .eq("is_template", false);

    // Filter out archived agents by default unless explicitly included
    if (!params.includeArchived) {
      query = query.neq("archived" as any, true);
    }

    query = query.order("created_at", { ascending: false });

    // Add search filter if provided
    if (params.search) {
      const searchTerm = params.search.toLowerCase();
      query = query.or(
        `title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
      );
    }

    const { data, error } = await query;

    // Handle conversation counts if requested
    let conversationCounts: Record<string, number> = {};
    if (params.includeConversationCounts && data && data.length > 0) {
      const agentArray: TemplateAgent[] = Array.isArray(data)
        ? (data as unknown[]).filter(isTemplateAgent) as TemplateAgent[]
        : [];
      const agentIds = agentArray.map((agent) => agent.id);
      const { data: conversationsData, error: conversationsError } =
        await supabase
          .from("conversations")
          .select("agent_id, agent_chat_user_id")
          .in("agent_id", agentIds)
          .not("agent_chat_user_id", "is", null); // Only include conversations with a valid agent_chat_user_id

      if (!conversationsError && conversationsData) {
        // Initialize counts object with zeros for all agent IDs
        if (Array.isArray(agentIds)) {
          conversationCounts = agentIds.reduce(
            (counts: Record<string, number>, id: string) => {
              counts[id] = 0;
              return counts;
            },
            {}
          );
        }

        // Count conversations for each agent, only counting those with a valid user
        conversationsData.forEach((item) => {
          if (
            item &&
            typeof item === 'object' &&
            'agent_id' in item &&
            typeof item.agent_id === 'string' &&
            conversationCounts[item.agent_id] !== undefined &&
            item.agent_chat_user_id
          ) {
            conversationCounts[item.agent_id]++;
          }
        });
      }
    }

    const result = {
      data,
      conversationCounts,
      error,
    };

    // Cache the result
    if (!error) {
      // Store in recent search cache
      recentSearchCache.set(cacheKey, {
        timestamp: Date.now(),
        result,
      });

      // If this is a basic fetch, update the agent cache
      if (isBasicFetch && Array.isArray(data)) {
        agentCache = {
          timestamp: Date.now(),
          data: (data as unknown[]).filter((item) => {
            return (
              item !== null &&
              typeof item === 'object' &&
              'id' in item &&
              'title' in item &&
              'organization_id' in item &&
              'created_by' in item
            );
          }) as TemplateAgent[],
        };
      } else if (isBasicFetch) {
        agentCache = {
          timestamp: Date.now(),
          data: [],
        };
      }

      // Clean up old cache entries (keep the cache size reasonable)
      if (recentSearchCache.size > 20) {
        const now = Date.now();
        for (const [key, value] of recentSearchCache.entries()) {
          if (now - value.timestamp > CACHE_EXPIRY_MS) {
            recentSearchCache.delete(key);
          }
        }
      }
    }

    if (error) {
      console.error("Error fetching agents:", error);
    }

    return result;
  } catch (error: unknown) {
    console.error("Error fetching agents:", error);
    return { error, conversationCounts: {} };
  }
};

// Clear all caches - useful when agents might have been updated
export const clearAllCaches = () => {
  templateAgentCache = null;
  agentCache = null;
  recentSearchCache.clear();
  console.log("[templateService] All caches cleared");
};

// Function to delete an agent using the agent-service edge function
export const deleteAgent = async (agentId: string, organizationId: string) => {
  try {
    const { functionsUrl, anonKey } = getSupabaseUrls();

    if (!functionsUrl) {
      throw new Error("VITE_SUPABASE_URL environment variable is not set");
    }

    console.log(
      `[templateService] Deleting agent: ${agentId} for organization: ${organizationId}`
    );

    const response = await fetch(`${functionsUrl}/agent-service`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        id: agentId,
        organization_id: organizationId,
      }),
    });

    const result = await response.json();

    if (result.error) {
      console.error("Error deleting agent (edge function):", result.error);
      return { error: result.error };
    }

    // Clear caches since an agent was deleted
    clearAllCaches();

    return { success: true };
  } catch (error: unknown) {
    console.error("Error deleting agent (edge function):", error);
    return { error };
  }
};

// Get a specific template by ID
export const getTemplateById = async (templateId: string) => {
  try {
    // Check cache first
    if (templateAgentCache && Array.isArray(templateAgentCache.data)) {
      const cachedTemplate = templateAgentCache.data.find(
        (t) => isTemplateAgent(t) && t.id === templateId
      );
      if (cachedTemplate) {
        console.log("[templateService] Using cached template");
        return { data: cachedTemplate };
      }
    }

    // Fetch from database if not in cache
    const { data, error } = await supabase
      .from("agents")
      .select("*")
      .eq("id", templateId)
      .eq("is_template", true)
      .single();

    if (error) {
      console.error("Error fetching template by ID:", error);
      return { error };
    }

    return { data };
  } catch (error: unknown) {
    console.error("Error fetching template by ID:", error);
    return { error };
  }
};

// Update an existing template
export const updateTemplate = async (
  templateId: string,
  templateData: Partial<TemplateAgent>
) => {
  try {
    const { data, error } = await supabase
      .from("agents")
      .update(templateData)
      .eq("id", templateId)
      .eq("is_template", true)
      .select()
      .single();

    if (error) {
      console.error("Error updating template:", error);
      return { error };
    }

    // Clear the cache since a template was updated
    clearTemplateCache();

    return { data };
  } catch (error: unknown) {
    console.error("Error updating template:", error);
    return { error };
  }
};

/**
 * Duplicate a template to create a new agent with all associated data
 * This is the primary function for agent creation from templates
 * Uses transaction-like behavior with rollback on failure
 */
export const duplicateTemplateToAgent = async (
  templateId: string,
  userId: string,
  organizationId: string
): Promise<{ success: boolean; agentId?: string; agentPublicId?: string; error?: string; data?: any }> => {
  // Input validation
  if (!templateId?.trim()) {
    console.error("[duplicateTemplateToAgent] Template ID is required");
    return { success: false, error: "Template ID is required" };
  }
  if (!userId?.trim()) {
    console.error("[duplicateTemplateToAgent] User ID is required");
    return { success: false, error: "User ID is required" };
  }
  if (!organizationId?.trim()) {
    console.error("[duplicateTemplateToAgent] Organization ID is required");
    return { success: false, error: "Organization ID is required" };
  }

  let createdAgentId: string | null = null;
  
  try {
    console.log(`[duplicateTemplateToAgent] Starting duplication of template ${templateId}`);

    // Get the current user to access their email and organization data
    const [userResult, orgResult] = await Promise.all([
      supabase.auth.getUser(),
      supabase
        .from("organizations")
        .select("name, industry_id")
        .eq("id", organizationId)
        .single()
    ]);
    
    const userEmail = userResult.data.user?.email || "";
    const organization = orgResult.data;
    
    if (orgResult.error) {
      console.warn(`[duplicateTemplateToAgent] Could not fetch organization data: ${orgResult.error.message}`);
    }

    // Get the template
    const { data: template, error: templateError } = await supabase
      .from("agents")
      .select("*")
      .eq("id", templateId)
      .single();

    if (templateError) throw new Error(`Template fetch failed: ${templateError.message}`);
    if (!template) throw new Error("Template not found");

    console.log(`[duplicateTemplateToAgent] Found template: ${template.title}`);

    // Get persona information if template has a persona_id
    let persona = null;
    if (template.persona_id) {
      const { data: personaData, error: personaError } = await supabase
        .from("personas")
        .select("avatar_name, avatar_image_url")
        .eq("id", template.persona_id)
        .single();
      
      if (!personaError && personaData) {
        persona = personaData;
        console.log(`[duplicateTemplateToAgent] Found persona: ${persona.avatar_name}`);
      }
    }

    // Get associated data in parallel for better performance
    const [formFieldsResult, behaviorsResult] = await Promise.all([
      supabase
        .from("form_fields")
        .select("*")
        .eq("agent_id", templateId),
      supabase
        .from("behaviors")
        .select("*")
        .eq("agent_id", templateId)
    ]);

    // Check for errors in parallel fetches
    if (formFieldsResult.error) {
      throw new Error(`Form fields fetch failed: ${formFieldsResult.error.message}`);
    }
    if (behaviorsResult.error) {
      throw new Error(`Behaviors fetch failed: ${behaviorsResult.error.message}`);
    }

    const formFields = formFieldsResult.data || [];
    const behaviors = behaviorsResult.data || [];

    console.log(`[duplicateTemplateToAgent] Found ${formFields.length} form fields and ${behaviors.length} behaviors`);

    // Apply defaults to template data, filling in missing values
    const templateWithDefaults = applyAgentDefaults(
      {
        // Basic info
        title: template.title,
        description: template.description,
        // Use persona name if available and template doesn't have agent_name, otherwise use template agent_name
        agent_name: persona?.avatar_name || template.agent_name,
        
        // Template relationships
        persona_id: template.persona_id || null,
        industry_id: template.industry_id || null,
        template_source_id: templateId,
        
        // Content and behavior
        welcome_message: template.welcome_message,
        conversation_starters: template.conversation_starters,
        thank_you_message: template.thank_you_message,
        instructions: template.instructions,
        restrictions: template.restrictions,
        hidden_template_instructions: template.hidden_template_instructions,
        
        // Visual elements
        // Use persona avatar if available, otherwise use template avatar
        avatar_url: persona?.avatar_image_url || template.avatar_url,
        banner: template.banner,
        
        // Splash page settings
        splash_page_title: template.splash_page_title,
        splash_page_subtitle: template.splash_page_subtitle,
        start_button_text: template.start_button_text,
        
        // AI settings
        ai_temperature: template.ai_temperature,
        
        // Features
        allow_file_search: template.allow_file_search,
        allow_web_search: template.allow_web_search,
        
        // Flags and settings
        is_template: false,
        is_active: true,
        is_public: template.is_public || false,
        goal: template.goal,
        category: template.category,
      },
      organization?.name || "Our Company",
      userEmail,
      organization?.industry_id
    );

    // Create new agent from template with defaults applied
    const { data: newAgent, error: agentError } = await supabase
      .from("agents")
      .insert({
        ...templateWithDefaults,
        // Organization and user info (always override)
        organization_id: organizationId,
        created_by: userId,
        // Notification settings - prefill with user's email
        notification_email: userEmail,
        // Generate new public_id
        public_id: crypto.randomUUID(),
      })
      .select()
      .single();

    if (agentError) throw new Error(`Agent creation failed: ${agentError.message}`);
    
    createdAgentId = newAgent.id;
    console.log(`[duplicateTemplateToAgent] Created new agent: ${newAgent.id}`);

    // Copy form fields if any exist, otherwise create default ones
    if (formFields.length > 0) {
      console.log(`[duplicateTemplateToAgent] Copying ${formFields.length} form fields`);
      
      const formFieldsToInsert = formFields.map((field) => ({
        title: field.title,
        field_type: field.field_type,
        answer_type: field.answer_type,
        instructions: field.instructions,
        is_required: field.is_required,
        options: field.options,
        position: field.position,
        agent_id: newAgent.id,
      }));

      const { error: formFieldsError } = await supabase
        .from("form_fields")
        .insert(formFieldsToInsert);

      if (formFieldsError) {
        throw new Error(`Form fields creation failed: ${formFieldsError.message}`);
      }
      
      console.log(`[duplicateTemplateToAgent] Successfully copied ${formFields.length} form fields`);
    } else {
      // Create default form fields (name, email, phone)
      console.log(`[duplicateTemplateToAgent] Creating default form fields`);
      
      const defaultFields = getDefaultFormFields().map(field => ({
        ...field,
        agent_id: newAgent.id,
      }));

      const { error: defaultFieldsError } = await supabase
        .from("form_fields")
        .insert(defaultFields);

      if (defaultFieldsError) {
        throw new Error(`Default form fields creation failed: ${defaultFieldsError.message}`);
      }
      
      console.log(`[duplicateTemplateToAgent] Successfully created ${defaultFields.length} default form fields`);
    }

    // Copy behaviors if any exist
    if (behaviors.length > 0) {
      console.log(`[duplicateTemplateToAgent] Copying ${behaviors.length} behaviors`);
      
      const behaviorsToInsert = behaviors.map((behavior) => ({
        title: behavior.title,
        description: behavior.description,
        comment: behavior.comment,
        agent_id: newAgent.id,           // Link to NEW agent
        organization_id: organizationId, // Use target organization
        // id, created_at, updated_at will be auto-generated
      }));

      const { error: behaviorsError } = await supabase
        .from("behaviors")
        .insert(behaviorsToInsert);

      if (behaviorsError) {
        throw new Error(`Behaviors creation failed: ${behaviorsError.message}`);
      }
      
      console.log(`[duplicateTemplateToAgent] Successfully copied ${behaviors.length} behaviors`);
    }

    console.log(`[duplicateTemplateToAgent] Successfully completed duplication. New agent ID: ${newAgent.id}`);
    return { success: true, agentId: newAgent.id, agentPublicId: newAgent.public_id, data: newAgent };

  } catch (error: any) {
    console.error("[duplicateTemplateToAgent] Error during duplication:", error);
    
    // Rollback: Delete the created agent if it exists
    if (createdAgentId) {
      console.log(`[duplicateTemplateToAgent] Rolling back - deleting agent ${createdAgentId}`);
      try {
        // Delete associated form fields first (foreign key constraint)
        await supabase
          .from("form_fields")
          .delete()
          .eq("agent_id", createdAgentId);
          
        // Delete associated behaviors (foreign key constraint)
        await supabase
          .from("behaviors")
          .delete()
          .eq("agent_id", createdAgentId);
          
        // Delete the agent
        await supabase
          .from("agents")
          .delete()
          .eq("id", createdAgentId);
          
        console.log(`[duplicateTemplateToAgent] Successfully rolled back agent ${createdAgentId}`);
      } catch (rollbackError) {
        console.error(`[duplicateTemplateToAgent] Rollback failed for agent ${createdAgentId}:`, rollbackError);
        // Don't throw here - we want to return the original error
      }
    }
    
    return { 
      success: false, 
      error: error.message || "Failed to duplicate template" 
    };
  } finally {
    // Clear caches in all cases to prevent stale data
    clearAllCaches();
  }
};

// Function to archive an agent
export const archiveAgent = async (agentId: string, organizationId: string) => {
  try {
    console.log(`[templateService] Archiving agent: ${agentId} for organization: ${organizationId}`);

    const { data, error } = await supabase
      .from("agents")
      .update({ archived: true } as any)
      .eq("id", agentId)
      .eq("organization_id", organizationId)
      .select()
      .single();

    if (error) {
      console.error("Error archiving agent:", error);
      return { error };
    }

    // decrease the active_agents_count by 1
    // First, fetch the current count
    const { data: orgData, error: orgError } = await supabase
      .from("organizations")
      .select("active_agents_count")
      .eq("id", organizationId)
      .single();

    if (!orgError && orgData && typeof orgData.active_agents_count === "number") {
      const newCount = Math.max(0, orgData.active_agents_count - 1);
      const { error: updateError } = await supabase
        .from("organizations")
        .update({ active_agents_count: newCount })
        .eq("id", organizationId);

      if (updateError) {
        console.error("Error updating organization:", updateError);
      }
    } else if (orgError) {
      console.error("Error fetching organization for agent count update:", orgError);
    }

    // Clear caches since an agent was archived
    clearAllCaches();

    return { success: true, data };
  } catch (error: unknown) {
    console.error("Error archiving agent:", error);
    return { error };
  }
};

// Function to unarchive an agent
export const unarchiveAgent = async (agentId: string, organizationId: string) => {
  try {
    console.log(`[templateService] Unarchiving agent: ${agentId} for organization: ${organizationId}`);

    // Check plan limits before unarchiving
    const planLimitError = await checkPlanLimits(organizationId, "active_agents");
    if (planLimitError) {
      return { error: "plan_limit_reached" };
    }

    const { data, error } = await supabase
      .from("agents")
      .update({ archived: false } as any)
      .eq("id", agentId)
      .eq("organization_id", organizationId)
      .select()
      .single();

    if (error) {
      console.error("Error unarchiving agent:", error);
      return { error };
    }

    // After unarchiving, increment active_agents_count by 1
    // First, fetch the current count
    const { data: orgData, error: orgError } = await supabase
      .from("organizations")
      .select("active_agents_count")
      .eq("id", organizationId)
      .single();

    if (!orgError && orgData && typeof orgData.active_agents_count === "number") {
      const newCount = orgData.active_agents_count + 1;
      const { error: updateError } = await supabase
        .from("organizations")
        .update({ active_agents_count: newCount })
        .eq("id", organizationId);

      if (updateError) {
        console.error("Error updating organization (increment):", updateError);
      }
    } else if (orgError) {
      console.error("Error fetching organization for agent count update (increment):", orgError);
    }

    // Clear caches since an agent was unarchived
    clearAllCaches();

    return { success: true, data };
  } catch (error: unknown) {
    console.error("Error unarchiving agent:", error);
    return { error };
  }
};

// Function to fetch archived agents
export const fetchArchivedAgents = async (organizationId: string) => {
  try {
    console.log(`[templateService] Fetching archived agents for organization: ${organizationId}`);

    const { data, error } = await supabase
      .from("agents")
      .select("*" as any)
      .eq("organization_id", organizationId)
      .eq("is_template", false)
      .eq("archived", true)
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("Error fetching archived agents:", error);
      return { error, data: [] };
    }

    return { data: data || [], error: null };
  } catch (error: unknown) {
    console.error("Error fetching archived agents:", error);
    return { error, data: [] };
  }
};